import type { LoaderFunctionArgs } from 'react-router-dom';
import { contracts } from '@immo/api-contract';
import {
  parseCreateFrom,
  parseOpenAppointment,
  type CreateFrom,
  type OpenAppointment,
} from '@/lib/calendar/linkable-records';
import { loadWorkspaceContext } from '@/lib/auth/workspace-context';
import { mayCall } from '@/lib/permissions/route-gate';

export type CalendarHomeLoaderData = {
  readonly workspaceId: string;
  readonly workspaceSlug: string;
  readonly selectedOrganizationId: string;
  readonly selectedPermissions: ReadonlyArray<string>;
  readonly canConnectMailbox: boolean;
  readonly canWriteAppointments: boolean;
  readonly canReadMembers: boolean;
  readonly canReadContacts: boolean;
  readonly canReadViewings: boolean;
  readonly createFrom: CreateFrom | null;
  readonly openAppointment: OpenAppointment | null;
};

// Creating, editing, dragging and cancelling all gate on `appointment-write`, the literal those
// routes declare (BL-099).
// The attendee field fires `members.list` and `contacts.workspaceList`, each gated on its own literal.
// The composer Besichtigungen fire `activities.viewings`, gated on its own literal (`activity-read`).
// The connect CTA fires `mailbox.connect`, so it gates on that route's own literal
// (`mailbox-connect`), not on the calendar-read the page itself needs (BL-099).
export async function calendarHomeLoader({ params, request }: LoaderFunctionArgs): Promise<CalendarHomeLoaderData> {
  const { workspaceId, workspaceSlug, organization, permissions } = await loadWorkspaceContext(params.workspaceId);
  const searchParams = new URL(request.url).searchParams;

  return {
    workspaceId,
    workspaceSlug,
    selectedOrganizationId: organization.id,
    selectedPermissions: permissions,
    canConnectMailbox: mayCall(permissions, contracts.mailbox.connect),
    canWriteAppointments: mayCall(permissions, contracts.calendar.createEvent),
    canReadMembers: mayCall(permissions, contracts.members.list),
    canReadContacts: mayCall(permissions, contracts.contacts.workspaceList),
    canReadViewings: mayCall(permissions, contracts.activities.viewings),
    createFrom: parseCreateFrom(searchParams),
    openAppointment: parseOpenAppointment(searchParams),
  };
}
